import PropTypes from 'prop-types';
import { useIsMobile } from '@bwoty-web/ui-kit/hooks';
import { UI_CHECKMARK } from '@bwoty-web/ui-kit/icons';
import { ArrowLink, BaseCard, Icon, Rating, Typography } from '@bwoty-web/ui-kit';

import { standaloneUrlHandler } from '../../../utils/url/url';

import './compactCruiseItem.scss';

const CompactCruiseItem = ({ cruise, isSummary = false, labels }) => {
  const {
    cabins,
    changeCabinLink,
    cruiseDetailsLink,
    cruiseItinerarySubTitle,
    cruiseListImage,
    cruiseShipName,
    cruiseShipRating,
    fromDate,
    itinerary,
    mealText,
    selectedCabinKey,
    toDate,
    usps,
  } = cruise;
  const isMobile = useIsMobile();

  const cabinSelected = cabins?.find(
    ({ key, fareTypePrices }) =>
      key === selectedCabinKey || fareTypePrices?.some(({ key: fareKey }) => fareKey === selectedCabinKey),
  );
  const showBeverageText = cabinSelected?.fareTypePrices?.some(({ beverageIncluded }) => beverageIncluded) ?? false;
  const selectedFareTypePrice = cabinSelected?.fareTypePrices?.find(({ key }) => key === selectedCabinKey);
  const cruisePopUpUrl = standaloneUrlHandler(`${cruiseDetailsLink}&pageTemplate=popUpPage&qf=popUpPage`);

  if (!cabinSelected) {
    return null;
  }

  const period = `${fromDate} - ${toDate}`;
  const itinerarySubTitleText = `${cruiseItinerarySubTitle} ${
    itinerary?.filter((stop) => stop?.resort !== '')?.length <= 4 ? '' : labels?.plusMoreText
  }`;
  return (
    <BaseCard
      image={cruiseListImage}
      imagePosition={isMobile ? 'top' : 'left'}
      imageAspectRatio={isMobile ? 16 / 9 : 4 / 3}
    >
      <section className="iw-compact-cruise-item__info-container">
        <div className="iw-compact-cruise-item__top-info">
          <Typography
            as="p"
            variant="mini"
            className="iw-compact-cruise-item__period"
          >
            {period}
          </Typography>
          <div className="iw-compact-cruise-item__info-header-container">
            {cruiseDetailsLink ? (
              <a
                className="iw-compact-cruise-item__info-header-link"
                href={cruisePopUpUrl}
              >
                <h3 className="iw-compact-cruise-item__info-header">{itinerarySubTitleText}</h3>
              </a>
            ) : (
              <h3 className="iw-compact-cruise-item__info-header">{itinerarySubTitleText}</h3>
            )}
            <p className="iw-compact-cruise-item__info-subheader">{cruiseShipName}</p>
          </div>
          {!!cruiseShipRating && (
            <div className="iw-compact-cruise-item__rating-container">
              <Rating
                rating={cruiseShipRating}
                size="s"
              />
            </div>
          )}
          {!isSummary && usps.length > 0 && (
            <ul className="iw-compact-cruise-item__bullets">
              {usps.map((usp) => (
                <li
                  key={usp}
                  className="iw-compact-cruise-item__bullet"
                >
                  {usp}
                </li>
              ))}
            </ul>
          )}
          {!isSummary && mealText && (
            <ul className="iw-compact-cruise-item__ksps">
              <li className="iw-compact-cruise-item__ksp">
                <Icon path={UI_CHECKMARK} />
                {mealText}
              </li>
            </ul>
          )}
          {labels?.readMoreCruise && <ArrowLink href={cruisePopUpUrl}>{labels?.readMoreCruise}</ArrowLink>}
        </div>
        {isSummary && (
          <div className="iw-compact-cruise-item__bottom-info">
            <div className="iw-compact-cruise-item__cabin-container">
              <p className="iw-compact-cruise-item__label">{labels?.cabinTypeTitle}</p>
              <p className="iw-compact-cruise-item__value">
                {`${cabinSelected?.cabinName}${
                  showBeverageText && selectedFareTypePrice
                    ? ` (${selectedFareTypePrice.beverageIncluded ? labels.drinkPackageIncluded : labels.drinkPackageExcluded})`
                    : ''
                }`}
              </p>
            </div>
            <ArrowLink href={standaloneUrlHandler(changeCabinLink)}>{labels?.changeCabin}</ArrowLink>
          </div>
        )}
      </section>
    </BaseCard>
  );
};

CompactCruiseItem.propTypes = {
  cruise: PropTypes.shape({
    cabins: PropTypes.arrayOf(
      PropTypes.shape({
        cabinName: PropTypes.string,
        key: PropTypes.string,
      }),
    ),
    changeCabinLink: PropTypes.string,
    cruiseDetailsLink: PropTypes.string,
    cruiseItinerarySubTitle: PropTypes.string,
    cruiseListImage: PropTypes.shape({
      path: PropTypes.string,
    }),
    cruiseShipName: PropTypes.string,
    cruiseShipRating: PropTypes.number,
    fromDate: PropTypes.string,
    itinerary: PropTypes.arrayOf(
      PropTypes.shape({
        resort: PropTypes.string,
      }),
    ),
    mealText: PropTypes.string,
    selectedCabinKey: PropTypes.string,
    toDate: PropTypes.string,
    usps: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  isSummary: PropTypes.bool,
  labels: PropTypes.shape({
    cabinTypeTitle: PropTypes.string,
    changeCabin: PropTypes.string,
    drinkPackageExcluded: PropTypes.string,
    drinkPackageIncluded: PropTypes.string,
    numberOfSuffix: PropTypes.string,
    plusMoreText: PropTypes.string,
    readMoreCruise: PropTypes.string,
  }).isRequired,
};

export default CompactCruiseItem;
